class CustomRecommendationList extends HTMLElement {
  #viewport;
  #items = [];
  #dots = [];
  #activeIndex = 0;
  #abortController;
  #resizeObserver;
  #scrollFrame;

  connectedCallback() {
    if (this.#abortController) return;

    this.#viewport = this.querySelector('[data-custom-recommendation-viewport]');
    this.#items = Array.from(this.querySelectorAll('[data-custom-recommendation-item]')).slice(0, 4);

    if (!this.#viewport || this.#items.length === 0) return;

    this.#abortController = new AbortController();
    this.#renderPagination();
    this.#bindEvents();
    this.#sync(0);
  }

  disconnectedCallback() {
    this.#abortController?.abort();
    this.#resizeObserver?.disconnect();
    window.cancelAnimationFrame(this.#scrollFrame);
    this.#abortController = null;
    this.#resizeObserver = null;
  }

  #renderPagination() {
    const pagination = this.querySelector('[data-custom-recommendation-pagination]');
    if (!pagination) return;

    const liveRegion = pagination.querySelector('[data-custom-recommendation-live]');
    this.#dots = this.#items.map((item, index) => {
      const dot = document.createElement('button');
      dot.type = 'button';
      dot.className = 'custom-recommendation-list__dot';
      dot.dataset.customRecommendationDot = String(index);
      dot.setAttribute('aria-label', `Go to slide ${index + 1} of ${this.#items.length}`);
      dot.setAttribute('aria-controls', this.#viewport.id || this.#assignViewportId());
      pagination.insertBefore(dot, liveRegion);
      return dot;
    });
  }

  #assignViewportId() {
    const blockId = this.getAttribute('data-shopify-editor-block') || Math.random().toString(36).slice(2);
    const id = `CustomRecommendationViewport-${blockId.replace(/[^a-zA-Z0-9_-]/g, '')}`;
    this.#viewport.id = id;
    return id;
  }

  #bindEvents() {
    const { signal } = this.#abortController;

    this.#viewport.addEventListener('scroll', () => this.#scheduleSync(), { passive: true, signal });
    this.#viewport.addEventListener('keydown', (event) => this.#handleKeydown(event), { signal });

    this.#dots.forEach((dot, index) => {
      dot.addEventListener('click', () => this.#goTo(index), { signal });
    });

    this.#resizeObserver = new ResizeObserver(() => this.#goTo(this.#activeIndex, false));
    this.#resizeObserver.observe(this.#viewport);

    const desktopMedia = window.matchMedia('(min-width: 750px)');
    const reducedMotion = window.matchMedia('(prefers-reduced-motion: reduce)');
    const syncVideos = () => this.#syncVideos(desktopMedia.matches, reducedMotion.matches);

    desktopMedia.addEventListener('change', syncVideos, { signal });
    reducedMotion.addEventListener('change', syncVideos, { signal });
    syncVideos();
  }

  #syncVideos(isDesktop, reduceMotion) {
    this.querySelectorAll('.custom-recommendation-list__media--video').forEach((video) => {
      const isDesktopVideo = video.classList.contains('custom-recommendation-list__media--desktop');
      const isMobileVideo = video.classList.contains('custom-recommendation-list__media--mobile');
      const isActiveBreakpoint =
        (!isDesktopVideo && !isMobileVideo) || (isDesktopVideo && isDesktop) || (isMobileVideo && !isDesktop);

      if (!reduceMotion && isActiveBreakpoint) {
        video.play().catch(() => {});
      } else {
        video.pause();
      }
    });
  }

  #handleKeydown(event) {
    if (!['ArrowLeft', 'ArrowRight', 'Home', 'End'].includes(event.key)) return;

    event.preventDefault();
    let nextIndex = this.#activeIndex;

    if (event.key === 'ArrowLeft') nextIndex -= 1;
    if (event.key === 'ArrowRight') nextIndex += 1;
    if (event.key === 'Home') nextIndex = 0;
    if (event.key === 'End') nextIndex = this.#items.length - 1;

    this.#goTo(Math.max(0, Math.min(nextIndex, this.#items.length - 1)));
  }

  #goTo(index, smooth = true) {
    const item = this.#items[index];
    if (!item) return;

    this.#viewport.scrollTo({
      left: item.offsetLeft,
      behavior: smooth && !window.matchMedia('(prefers-reduced-motion: reduce)').matches ? 'smooth' : 'auto',
    });
    this.#sync(index);
  }

  #scheduleSync() {
    window.cancelAnimationFrame(this.#scrollFrame);
    this.#scrollFrame = window.requestAnimationFrame(() => {
      const viewportCenter = this.#viewport.scrollLeft + this.#viewport.clientWidth / 2;
      const index = this.#items.reduce((closestIndex, item, itemIndex) => {
        const itemCenter = item.offsetLeft + item.offsetWidth / 2;
        const closest = this.#items[closestIndex];
        const closestCenter = closest.offsetLeft + closest.offsetWidth / 2;
        return Math.abs(itemCenter - viewportCenter) < Math.abs(closestCenter - viewportCenter)
          ? itemIndex
          : closestIndex;
      }, 0);

      this.#sync(index);
    });
  }

  #sync(index) {
    this.#activeIndex = index;

    this.#dots.forEach((dot, dotIndex) => {
      dot.setAttribute('aria-current', dotIndex === index ? 'true' : 'false');
    });

    this.#items.forEach((item, itemIndex) => {
      item.setAttribute('aria-label', `${itemIndex + 1} of ${this.#items.length}`);
    });

    const liveRegion = this.querySelector('[data-custom-recommendation-live]');
    if (liveRegion) liveRegion.textContent = `Slide ${index + 1} of ${this.#items.length}`;
  }
}

if (!customElements.get('custom-recommendation-list')) {
  customElements.define('custom-recommendation-list', CustomRecommendationList);
}
